// src/config/storage.config.ts
// Configuração centralizada para o serviço de storage de imagens

/**
 * INSTRUÇÕES DE CONFIGURAÇÃO:
 * 
 * 1. Crie uma conta gratuita no Cloudinary: https://cloudinary.com/
 * 2. Obtenha seu "Cloud Name" no dashboard
 * 3. Crie uma pasta chamada "yearbook" e faça upload das imagens
 * 4. Substitua 'YOUR_CLOUD_NAME' abaixo pelo seu cloud name
 * 
 * Estrutura de pastas no Cloudinary:
 * yearbook/
 *   ├── students/      (fotos dos alunos)
 *   ├── teachers/      (fotos dos professores)
 *   ├── gallery/       (galeria geral)
 *   └── moments/       (eventos por pasta)
 *       ├── trote-leite/
 *       ├── dia-mulher/
 *       ├── hackathon/
 *       └── ...
 */

// ============================================
// CONFIGURAÇÃO PRINCIPAL
// ============================================

// Tipos de provider suportados
export type StorageProvider = 'cloudinary' | 'imgbb' | 'firebase' | 'local';

export const STORAGE_CONFIG = {
  // Provedor atual - usando ImgBB
  // O código busca URLs no imageUrls.json
  provider: 'imgbb' as StorageProvider,
  
  // Cloudinary - disponível se quiser migrar no futuro
  cloudinary: {
    cloudName: 'YOUR_CLOUD_NAME',
    baseFolder: 'yearbook',
  },
  
  // ImgBB (configuração atual)
  imgbb: {
    useJsonMapping: true, // Usar o imageUrls.json existente
  },
  
  // Firebase Storage
  firebase: {
    bucket: 'your-project.appspot.com',
    baseFolder: 'yearbook',
  },
  
  // Fallback local (apenas desenvolvimento)
  local: {
    basePath: '/assets',
  },
};

// ============================================
// CLOUDINARY - TRANSFORMAÇÕES DE IMAGEM
// ============================================

export const IMAGE_TRANSFORMATIONS = {
  // Thumbnails para cards e grids
  thumbnail: {
    width: 300,
    height: 300,
    crop: 'fill',
    quality: 'auto',
    format: 'auto',
  },
  
  // Imagem média para visualização
  medium: {
    width: 800,
    quality: 'auto',
    format: 'auto',
  },
  
  // Imagem grande para modal/lightbox
  large: {
    width: 1200,
    quality: 'auto:best',
    format: 'auto',
  },
  
  // Avatar/foto de perfil
  avatar: {
    width: 200,
    height: 200,
    crop: 'thumb',
    gravity: 'face',
    quality: 'auto',
    format: 'auto',
  },
  
  // Responsive - ajusta automaticamente
  responsive: {
    width: 'auto',
    dpr: 'auto',
    quality: 'auto',
    format: 'auto',
  },
};

// ============================================
// CATEGORIAS DE IMAGENS
// ============================================

export const IMAGE_CATEGORIES = {
  STUDENTS: 'students',
  TEACHERS: 'teachers',
  GALLERY: 'gallery',
  MOMENTS: 'moments',
} as const;

export type ImageCategory = typeof IMAGE_CATEGORIES[keyof typeof IMAGE_CATEGORIES];

// ============================================
// MAPEAMENTO DE PASTAS ANTIGAS -> NOVAS
// ============================================

export const FOLDER_MAPPING: Record<string, string> = {
  // Mapeia nomes de pastas antigas para novas no Cloudinary
  'assetsAlunosImg': 'students',
  'fotosDosProfessores': 'teachers',
  'fotosGaleria': 'gallery',
  'fotosTroteLeite': 'moments/trote-leite',
  'fotosDiaMulher': 'moments/dia-mulher',
  'fotosLinhaTempo': 'moments/linha-tempo',
  'fotosQuiz': 'moments/quiz',
  'fotosBingo': 'moments/bingo',
  'fotosApae': 'moments/apae',
  'fotosArduino': 'moments/arduino',
  'fotosCic': 'moments/cic',
  'fotosDeployCarreira': 'moments/deploy-carreira',
  'fotosEtec': 'moments/etec',
  'fotosHackathon': 'moments/hackathon',
  'fotosInterAgro': 'moments/interagro',
  'fotosKelly': 'moments/kelly',
  'fotosMaratona': 'moments/maratona',
  'fotosMaratona2Fase': 'moments/maratona-2fase',
  'fotosMicro2000': 'moments/micro2000',
  'fotosPalestras': 'moments/palestras',
  'fotosPalestraSegurança': 'moments/palestra-seguranca',
  'fotosPI2Final': 'moments/pi2-final',
  'fotosProgramador': 'moments/programador',
  'fotosQuizVoltaAsAulas': 'moments/quiz-volta-aulas',
  'fotosAgasalho': 'moments/agasalho',
  'mostraTrabalhosAmsNRA': 'moments/mostra-trabalhos',
};
